import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export async function uploadToSiteAssets(file: File, folder = "uploads"): Promise<string | null> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from("site-assets")
    .upload(fileName, file, { cacheControl: "3600", upsert: false });
  if (error) {
    toast.error("Error al subir la imagen: " + error.message);
    return null;
  }
  const { data } = supabase.storage.from("site-assets").getPublicUrl(fileName);
  return data.publicUrl;
}

/** Single image field: paste a URL or upload a local file. */
export function ImageUploadField({
  value,
  onChange,
  folder = "uploads",
  placeholder = "https://... o sube una imagen",
}: {
  value: string;
  onChange: (url: string) => void;
  folder?: string;
  placeholder?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadToSiteAssets(file, folder);
    setUploading(false);
    if (url) {
      onChange(url);
      toast.success("Imagen subida");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span className="ml-2 hidden sm:inline">Subir</span>
        </Button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
      {value && (
        <img src={value} className="h-20 w-32 object-cover rounded border border-border" alt="Vista previa" />
      )}
    </div>
  );
}

/** Gallery field: add images by URL or upload local files (multiple). */
export function GalleryUploadField({
  items,
  onChange,
  folder = "gallery",
}: {
  items: string[];
  onChange: (items: string[]) => void;
  folder?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const addUrl = () => {
    if (url.trim()) {
      onChange([...items, url.trim()]);
      setUrl("");
    }
  };

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded: string[] = [];
    for (const file of files) {
      const u = await uploadToSiteAssets(file, folder);
      if (u) uploaded.push(u);
    }
    setUploading(false);
    if (uploaded.length) {
      onChange([...items, ...uploaded]);
      toast.success(`${uploaded.length} imagen(es) subida(s)`);
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="URL de imagen"
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())}
        />
        <Button type="button" size="sm" onClick={addUrl}>
          +
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFiles}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((img, i) => (
          <div key={i} className="relative group">
            <img src={img} className="h-14 w-20 object-cover rounded border border-border" alt="" />
            <button
              type="button"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-4 w-4 flex items-center justify-center text-xs"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
